import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
const compact = require('lodash/compact');
const sortBy = require('lodash/sortBy');

import AccessibleIcon from '../common/AccessibleIcon';
import ActiveTagTableRow from './ActiveTagTableRow';
import NewTagTableRow from './NewTagTableRow';
import Tooltip from '../common/Tooltip';

const propTypes = {
    className: PropTypes.string,
    tags: PropTypes.array,
    onDeleteTag: PropTypes.func.isRequired,
    onNewTagsAdd: PropTypes.func.isRequired,
    pendingAddNewTag: PropTypes.bool,
    orgIntegrationTags: PropTypes.array.isRequired,
    activeTagExportHref: PropTypes.string,
    duplicateTagsIds: PropTypes.arrayOf(PropTypes.string),
    duplicateTooltipMessage: PropTypes.string,
};

const defaultProps = {
    className: '',
    tags: [],
    pendingAddNewTag: false,
    activeTagExportHref: '',
    duplicateTagsIds: [],
    duplicateTooltipMessage: '',
};

class TagEditorTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            newTags: {},
            tagFilter: '',
            newTagFilter: '',
        };

        this.onTagFilterChange = this.onTagFilterChange.bind(this);
        this.onNewTagFitlerChange = this.onNewTagFitlerChange.bind(this);
        this.onNewTagsAdd = this.onNewTagsAdd.bind(this);
        this.onSelectTag = this.onSelectTag.bind(this);
    }

    onTagFilterChange(e) {
        this.setState({ tagFilter: e.target.value });
    }

    onNewTagFitlerChange(e) {
        this.setState({ newTagFilter: e.target.value });
    }

    onNewTagsAdd() {
        const newTags = Object.values(this.state.newTags);
        if (newTags.length === 0) return;

        this.props.onNewTagsAdd(newTags);
        this.setState({ newTags: {}, tagFilter: '' });
    }

    onSelectTag(tagRecord, e) {
        if (e.target.checked) {
            this.setState({ newTags: {...this.state.newTags, [tagRecord.tag]: tagRecord } })
        }
        else {
            let updatedNewTags = { ...this.state.newTags };
            delete updatedNewTags[tagRecord.tag];
            this.setState({ newTags: updatedNewTags });
        }
    }

    render() {
        const self = this;
        const tagRecordMatchesFilter = (tagRecord, filterTerm) => {
            return (
                tagRecord.tag.indexOf(filterTerm) >= 0
                || (
                    !!tagRecord.description
                    && tagRecord.description.toLowerCase().indexOf(filterTerm.toLowerCase()) >= 0
                )
            );
        }

        let filteredActiveTags = this.props.tags;
        if (!!this.state.tagFilter) {
            filteredActiveTags = this.props.tags.filter((activeTag) => {
                const tagRecord = this.props.orgIntegrationTags.find(t => t.tag === activeTag.tag);
                return tagRecordMatchesFilter(tagRecord, this.state.tagFilter);
            });
        }

        const sortedFilteredActiveTags = sortBy(filteredActiveTags, t => t.tag.toLowerCase());


        let filteredAvailableTags = this.props.orgIntegrationTags;
        if (!!this.state.newTagFilter) {
            filteredAvailableTags = this.props.orgIntegrationTags.filter((availTag) => {
                const matchesFilter = tagRecordMatchesFilter(availTag, this.state.newTagFilter);
                const selected = !!this.state.newTags[availTag.tag];
                availTag.matchesFilter = matchesFilter;
                return matchesFilter || selected;
            });
        }

        const selectableTags = compact(filteredAvailableTags.map((tagRecord) => {
            if (self.props.tags.find(t => t.tag === tagRecord.tag)) { // remove already-in-use tags
                return null;
            }

            return tagRecord;
        }));
        const sortedFilteredAvailableTags = sortBy(selectableTags, tagRecord => tagRecord.tag.toLowerCase());

        return (
            <div className={this.props.className}>
                <div className="margin-bottom-md">
                    <div className="termListing">
                        <h2 className="font-weight-bold">
                            Tags in use
                            {!!this.props.activeTagExportHref &&
                                <a
                                    className="link-plain bare-text-button glyph margin-left-md"
                                    href={this.props.activeTagExportHref}
                                >
                                    <AccessibleIcon label="Export CSV" showLabelAsTitle>
                                        <span className="fa fa-download" aria-hidden={true} />
                                    </AccessibleIcon>
                                </a>
                            }
                        </h2>

                        <input
                            className="rightAlignRow"
                            onChange={this.onTagFilterChange}
                            value={this.state.tagFilter}
                            placeholder="Search"
                        />
                    </div>
                </div>
                <ul className="tagTable--heightConstrained margin-bottom-lg">
                    {sortedFilteredActiveTags.map((activeTagRecord) => {
                        const tagRecord = this.props.orgIntegrationTags.find(t => t.tag === activeTagRecord.tag);
                        const isDuplicate = this.props.duplicateTagsIds.indexOf(activeTagRecord.id) >= 0;
                        let duplicateTooltipElem = null;
                        if (isDuplicate && this.props.duplicateTooltipMessage) {
                            duplicateTooltipElem = (
                                <Tooltip
                                    id={`editor-dupe-tag-${activeTagRecord.id}`}
                                    icon="fa-exclamation-circle"
                                    placement="right"
                                    className="margin-left-md"
                                >
                                    {this.props.duplicateTooltipMessage}
                                </Tooltip>
                            );
                        }
                        return (
                            <ActiveTagTableRow
                                key={tagRecord.id}
                                tagRecord={tagRecord}
                                tooltipNode={duplicateTooltipElem}
                                onDeleteTag={this.props.onDeleteTag.bind(null, tagRecord)}
                            />
                        );
                    })}
                    {sortedFilteredActiveTags.length === 0 && this.state.tagFilter &&
                        <li className="tagRow" key="none-found">
                            <div /> {/*first col spacing*/}
                            <em className="font-style-italic">
                                No matching tags found
                            </em>
                        </li>
                    }
                    {sortedFilteredActiveTags.length === 0 && !this.state.tagFilter &&
                        <li className="tagRow" key="none-found">
                            <div /> {/*first col spacing*/}
                            <em className="font-style-italic">
                                No tags in use
                            </em>
                        </li>
                    }
                </ul>
                <div className="paddint-top-md margin-bottom-md">
                    <div className="termListing">
                        <h2 className="termListing__termContainer font-weight-bold">
                            Available tags
                        </h2>
                        <input
                            className="rightAlignRow"
                            onChange={this.onNewTagFitlerChange}
                            value={this.state.newTagFilter}
                            placeholder="Search"
                        />
                    </div>
                </div>
                <ul className="tagTable--heightConstrained">
                    {sortedFilteredAvailableTags.map(tagRecord => {
                        return (
                            <NewTagTableRow
                                key={tagRecord.id}
                                tagRecord={tagRecord}
                                /*kept in filtered tags because it's selected but doesnt match filter*/
                                className={!!this.state.newTagFilter && !tagRecord.matchesFilter ? 'nonMatch' : ''}
                                selected={!!this.state.newTags[tagRecord.tag]}
                                onSelectTag={this.onSelectTag.bind(null, tagRecord)}
                            />
                        );
                    })}
                    {sortedFilteredAvailableTags.length === 0 && this.state.newTagFilter &&
                        <li className="tagRow" key="none-found">
                            <div /> {/*first col spacing*/}
                            <em className="font-style-italic">
                                No new, matching tags found
                            </em>
                        </li>
                    }
                    {sortedFilteredAvailableTags.length === 0 && !this.state.newTagFilter &&
                        <li className="tagRow" key="none-found">
                            <div /> {/*first col spacing*/}
                            <em className="font-style-italic">
                                No new tags available
                            </em>
                        </li>
                    }
                </ul>
                <div className="margin-top-md">
                    <Button
                        className="btn btn-primary fr"
                        onClick={this.onNewTagsAdd}
                        disabled={this.props.pendingAddNewTag || Object.keys(this.state.newTags).length === 0}
                    >
                        Add Selected
                    </Button>
                    <div className="float-clear" />
                </div>
            </div>
        );
    }
}

TagEditorTable.propTypes = propTypes;
TagEditorTable.defaultProps = defaultProps;

export default TagEditorTable;